import { StrictMode, useEffect, useState } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { ErrorBoundary } from "../src/shared/error-boundary/error-boundary";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { useRegisterSW } from 'virtual:pwa-register/react';

import type { Route } from "./+types/root";
import { isDevMode } from "../src/shared/config/dev-mode";
import { initializeMockTelegramWebApp } from "../src/shared/api/telegram-mock";
import { initializeTheme } from "../src/shared/config/theme";
import { initWebVitals } from "../src/shared/utils/web-vitals";
import { migrateStorageKeys } from "../src/shared/utils/storage-migration";
import { getQueryClient } from "./query-client";
import { ClientOnlyToastPortal, AuthLayoutWithTonConnect } from "./components";
import { useMixpanelInit, useMixpanelIdentifyTelegramUser } from "../src/shared/hooks";
import { i18n, initializeLanguage } from "../src/shared/config/i18n";
import { initSentry } from "../src/shared/services/sentry";
import { logger } from "../src/shared/utils/logger";

import "./app.css";

const queryClient = getQueryClient();

if (!import.meta.env.SSR) {
  setTimeout(() => {
    initSentry();
  }, 0);
}

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://tonmobile.xomobile.com" },
  { rel: "dns-prefetch", href: "https://tonmobile.xomobile.com" },
  { rel: "preload", href: "/telegram-web-app.js", as: "script" },
  { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "dns-prefetch",
    href: "https://fonts.gstatic.com",
  },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "preload",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
    as: "style",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  },
  { rel: "dns-prefetch", href: "https://o569482.ingest.us.sentry.io" },
  { rel: "dns-prefetch", href: "https://api-eu.mixpanel.com" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  // Get current language from i18n instance
  const currentLang = i18n.language || 'en';

  return (
    <html lang={currentLang} suppressHydrationWarning>
    <head>
      <meta charSet="utf-8"/>
      <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"/>
      <meta httpEquiv="X-Content-Type-Options" content="nosniff"/>
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block"/>
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin"/>
      <script src="/init.js"/>
      <link rel="stylesheet" href="/loader.css"/>
      <Meta/>
      <Links/>
    </head>
    <body suppressHydrationWarning>
    {children}
    <ClientOnlyToastPortal/>
    <ScrollRestoration getKey={(location) => {
      // Use only pathname for scroll restoration key
      // This prevents scroll reset when only query params change (like tabs)
      return location.pathname;
    }}/>
    <Scripts/>
    </body>
    </html>
  );
}

export default function App() {
  const manifestUrl =
    (import.meta.env?.VITE_TONCONNECT_MANIFEST_URL as string | undefined) ||
    '/tonconnect-manifest.json';

  const [analyticsReady, setAnalyticsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnalyticsReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      logger.info('[SW] Registered', r);
    },
    onRegisterError(error) {
      logger.error('[SW] Registration failed', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      logger.info('[SW] Update available');
      if (window.confirm('Доступно обновление приложения. Обновить сейчас?')) {
        updateServiceWorker(true);
      }
    }
  }, [needRefresh, updateServiceWorker]);

  useMixpanelInit({ trackPageViews: true }, analyticsReady);
  useMixpanelIdentifyTelegramUser(analyticsReady);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    migrateStorageKeys();

    initWebVitals();

    if (isDevMode()) {
      initializeMockTelegramWebApp();
      logger.log("[Root] Dev mode enabled - using mock Telegram WebApp");
    }

    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.ready();
      tg.expand();
      tg.enableClosingConfirmation()
      tg.disableVerticalSwipes?.();
    }

    initializeTheme();
    initializeLanguage();
  }, []);

  return (
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AuthLayoutWithTonConnect manifestUrl={manifestUrl}>
            <Outlet/>
          </AuthLayoutWithTonConnect>
        </QueryClientProvider>
      </I18nextProvider>
    </StrictMode>
  );
}

export { ErrorBoundary };

export function HydrateFallback() {
  return null;
}
